import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "react-js-pagination";
import { IoSearch } from "react-icons/io5";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DownloadIcon from "@mui/icons-material/Download";
import IconButton from "../../components/Button/IconButton";
import { Input, TextArea } from "../../components/Form";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import { toastMessage } from "../../utils/toastMessage";
import findSerialNumber from "../../utils/findSerialNumber";
import Switch from "../../components/Switch/Switch";
import ModalBox from "../../components/modal/ModalBox";

import {
  fetchInfluencersApi,
  createInfluencerApi,
  updateInfluencerApi,
  deleteInfluencerApi,
  exportInfluencerEarningsApi,
} from "../../services/influencerManagement";

const EMPTY_FORM = {
  name: "", email: "", phone: "", sharePercent: "",
  notes: "",
  bankDetails: { accountHolderName: "", accountNumber: "", ifscCode: "", bankName: "", upiId: "" },
};

export default function InfluencerManagement() {
  const navigate = useNavigate();
  const [influencers, setInfluencers] = useState([]);
  const [total, setTotal]             = useState(0);
  const [activePage, setActivePage]   = useState(1);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [search, setSearch]           = useState("");
  const [showModal, setShowModal]     = useState(false);
  const [editId, setEditId]           = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [submitting, setSubmitting]   = useState(false);
  const [exportPeriod, setExportPeriod] = useState("today");
  const [exportFrom, setExportFrom]     = useState("");
  const [exportTo, setExportTo]         = useState("");
  const [exporting, setExporting]       = useState(false);

  const pageSize = 10;

  const load = useCallback(async () => {
    showLoader();
    try {
      const params = { page: activePage - 1, pageSize };
      if (search) params.search = search;
      const res = await fetchInfluencersApi(params);
      if (res.data.success) {
        setInfluencers(res.data.data.influencers || []);
        setTotal(res.data.data.total || 0);
      }
    } catch (e) {
      toastMessage("Failed to load influencers", "error");
    } finally {
      hideLoader();
    }
  }, [activePage, search]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY_FORM); setEditId(null); setShowModal(true); };
  const openEdit   = (inf) => {
    setForm({
      name: inf.name, email: inf.email, phone: inf.phone,
      sharePercent: inf.sharePercent, notes: inf.notes || "",
      bankDetails: inf.bankDetails || EMPTY_FORM.bankDetails,
    });
    setEditId(inf._id); setShowModal(true);
  };

  const setBank = (key, val) =>
    setForm(f => ({ ...f, bankDetails: { ...f.bankDetails, [key]: val } }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const payload = { ...form, sharePercent: parseFloat(form.sharePercent) };
      if (editId) await updateInfluencerApi(editId, payload);
      else        await createInfluencerApi(payload);
      toastMessage(editId ? "Influencer updated!" : "Influencer created!", "success");
      setShowModal(false);
      load();
    } catch (e) {
      toastMessage(e?.response?.data?.message || "Something went wrong.", "error");
    }
    setSubmitting(false);
  };

  const toggleStatus = async (id) => {
    try {
      await deleteInfluencerApi(id);
      load();
    } catch (e) {
      toastMessage("Failed to update status", "error");
    }
  };

  const handleExportEarnings = async () => {
    if (exportPeriod === "custom" && (!exportFrom || !exportTo)) {
      toastMessage("Please select both start and end dates for custom range", "error");
      return;
    }
    setExporting(true);
    try {
      const params = { period: exportPeriod };
      if (exportPeriod === "custom") {
        params.from = exportFrom;
        params.to = exportTo;
      }
      const response = await exportInfluencerEarningsApi(params);
      const contentDisposition = response.headers["content-disposition"];
      const matches = /filename="([^"]+)"/.exec(contentDisposition || "");
      const filename = matches?.[1] || `influencer_earnings_${exportPeriod}.xlsx`;
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toastMessage("Earnings report downloaded", "success");
    } catch (e) {
      toastMessage(e?.response?.data?.message || "Failed to export earnings", "error");
    } finally {
      setExporting(false);
    }
  };

  const PERIOD_OPTIONS = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "current_month", label: "Current Month" },
    { value: "last_month", label: "Last Month" },
    { value: "custom", label: "Custom Range" },
  ];

  return (
    <>
      {loader}
      <div className="WrapperArea">
        <div className="WrapperBox">
          <div className="TitleBox">
            <h4 className="Title">Influencer Management</h4>
            <div className="block_user_button">
              <button onClick={openCreate}>
                <AddIcon />
                Add Influencer
              </button>
            </div>
          </div>

          <div className="FilterBox FilterArea">
            <div className="form-group search_lg_size userSearch">
              <input
                type="search"
                className="form-control"
                placeholder="Search name, email, phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="user_search_icon">
                <IoSearch />
              </span>
            </div>

            <div className="d-flex flex-wrap align-items-end gap-2" style={{ marginLeft: "auto" }}>
              <div className="form-group mb-0">
                <label className="form-label" style={{ fontSize: "12px" }}>Earnings Export</label>
                <select
                  className="form-control"
                  value={exportPeriod}
                  onChange={(e) => setExportPeriod(e.target.value)}
                  style={{ minWidth: "160px" }}
                >
                  {PERIOD_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {exportPeriod === "custom" && (
                <>
                  <div className="form-group mb-0">
                    <label className="form-label" style={{ fontSize: "12px" }}>From</label>
                    <input
                      type="date"
                      className="form-control"
                      value={exportFrom}
                      onChange={(e) => setExportFrom(e.target.value)}
                    />
                  </div>
                  <div className="form-group mb-0">
                    <label className="form-label" style={{ fontSize: "12px" }}>To</label>
                    <input
                      type="date"
                      className="form-control"
                      value={exportTo}
                      onChange={(e) => setExportTo(e.target.value)}
                    />
                  </div>
                </>
              )}
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-1"
                onClick={handleExportEarnings}
                disabled={exporting}
                style={{ height: "38px", marginTop: exportPeriod === "custom" ? "22px" : "22px" }}
              >
                <DownloadIcon fontSize="small" />
                {exporting ? "Exporting…" : "Download XLS"}
              </button>
            </div>
          </div>

          <div className="content-table">
            <table>
              <thead>
                <tr>
                  <th>S. No.</th>
                  <th>Name</th>
                  <th>Contact Info</th>
                  <th>Earning Share</th>
                  <th>Join Date</th>
                  <th>Status</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {influencers.length > 0 ? (
                  influencers.map((inf, index) => (
                    <tr key={inf._id}>
                      <td>{findSerialNumber(index, activePage, pageSize)}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div style={{
                            width: "35px", height: "35px", borderRadius: "50%",
                            background: "#CD2F38", color: "#fff", display: "flex",
                            alignItems: "center", justifyContent: "center", marginRight: "10px",
                            fontWeight: "bold"
                          }}>
                            {inf.name?.[0]?.toUpperCase()}
                          </div>
                          <strong>{inf.name}</strong>
                        </div>
                      </td>
                      <td>
                        <div>{inf.email}</div>
                        <div style={{ fontSize: "12px", color: "#666" }}>📞 {inf.phone}</div>
                      </td>
                      <td>
                        <span className="badge badge-info" style={{ background: "#6c63ff", color: "#fff" }}>
                          {inf.sharePercent}%
                        </span>
                      </td>
                      <td>{new Date(inf.createdAt).toLocaleDateString("en-IN")}</td>
                      <td>
                        <Switch
                          checked={inf.isActive}
                          onChange={() => toggleStatus(inf._id)}
                        />
                      </td>
                      <td className="text-center">
                        <div className="action_button d-flex justify-content-center">
                          <IconButton onClick={() => navigate(`/influencer-management/detail/${inf._id}`)} title="View Dashboard">
                            <AssessmentIcon style={{ color: "#6c63ff" }} />
                          </IconButton>
                          <IconButton onClick={() => openEdit(inf)} title="Edit">
                            <EditIcon className="edit_icon" />
                          </IconButton>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={7} className="text-center py-4">No influencers found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {total > pageSize && (
            <div className="pagination-wrapper">
              <div className="Pagination">
                <Pagination
                  activePage={activePage}
                  itemsCountPerPage={pageSize}
                  totalItemsCount={total}
                  pageRangeDisplayed={5}
                  onChange={(p) => setActivePage(p)}
                  hideDisabled={true}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <ModalBox
        status={showModal}
        onClose={() => setShowModal(false)}
        title={editId ? "Edit Influencer" : "Add Influencer"}
        closeIcon
        size="lg"
        noFooter
      >
        <form onSubmit={handleSubmit}>
          <div className="grid-responsive">
            <div className="form-group">
              <Input
                label="Full Name"
                required
                className="form-control"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <Input
                label="Phone"
                required
                className="form-control"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <Input
                label="Email"
                type="email"
                required
                className="form-control"
                value={form.email}
                disabled={!!editId}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <Input
                label="Earning Share %"
                type="number"
                step="0.01"
                required
                className="form-control"
                value={form.sharePercent}
                onChange={(e) =>
                  setForm((f) => ({ ...f, sharePercent: e.target.value }))
                }
                placeholder="e.g. 10"
              />
            </div>

            <div className="form-group" style={{ gridColumn: "1 / -1", marginTop: "10px" }}>
              <h6 style={{ marginBottom: "8px", fontWeight: "700" }}>🏦 Bank / UPI Details</h6>
              <hr style={{ margin: "0 0 16px" }} />
            </div>

            <div className="form-group">
              <Input
                label="Account Holder"
                className="form-control"
                value={form.bankDetails.accountHolderName}
                onChange={(e) => setBank("accountHolderName", e.target.value)}
              />
            </div>
            <div className="form-group">
              <Input
                label="Account Number"
                className="form-control"
                value={form.bankDetails.accountNumber}
                onChange={(e) => setBank("accountNumber", e.target.value)}
              />
            </div>
            <div className="form-group">
              <Input
                label="IFSC Code"
                className="form-control"
                value={form.bankDetails.ifscCode}
                onChange={(e) => setBank("ifscCode", e.target.value)}
              />
            </div>
            <div className="form-group">
              <Input
                label="Bank Name"
                className="form-control"
                value={form.bankDetails.bankName}
                onChange={(e) => setBank("bankName", e.target.value)}
              />
            </div>
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <Input
                label="UPI ID"
                className="form-control"
                value={form.bankDetails.upiId}
                onChange={(e) => setBank("upiId", e.target.value)}
                placeholder="e.g. name@upi"
              />
            </div>

            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <TextArea
                label="Notes"
                className="form-control"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                rows={2}
              />
            </div>
            <div className="form-group" style={{ gridColumn: "1 / -1", marginTop: "1rem" }}>
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={submitting}
              >
                {submitting
                  ? "Saving..."
                  : editId
                    ? "Update Influencer"
                    : "Create Influencer"}
              </button>
            </div>
          </div>
        </form>
      </ModalBox>
    </>
  );
}
